import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { PageHeader } from "@/components/data-display/page-header";
import { StatCard } from "@/components/data-display/stat-card";
import { Badge } from "@/components/ui/badge";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { LoadingState } from "@/components/ui/system-state";
import { AdminConsoleFailureState } from "@/features/admin/admin-console-failure";
import {
  fetchDeploymentPosture,
  type AdminConsoleFailure,
  type DeploymentPosture,
  type LifecycleStage,
} from "@/lib/admin-console-api";

/**
 * M2/P1/I2 — Deployment (ALL-5803). What is running and whether it is ready, read from
 * the admin console's GET /admin/api/deployment: the release, commit and region of this
 * deployment, the four lifecycle stages with each check's status and evidence, and the
 * triage playbooks and external hand-offs. Read-only; the snapshot time is always shown
 * because the console probes on demand.
 */

type Load = { kind: "loading" } | { kind: "ready"; posture: DeploymentPosture } | { kind: "failed"; failure: AdminConsoleFailure };

const STAGE_STATUS: Record<LifecycleStage["status"], { label: string; tone: "success" | "warning" | "danger" | "neutral" }> = {
  ready: { label: "Ready", tone: "success" },
  in_progress: { label: "In progress", tone: "warning" },
  blocked: { label: "Blocked", tone: "danger" },
  unknown: { label: "Unknown", tone: "neutral" },
};

function checkTone(status: string): "success" | "warning" | "danger" | "neutral" {
  if (status === "ready" || status === "up") return "success";
  if (status === "in_progress") return "warning";
  if (status === "blocked" || status === "down" || status === "missing") return "danger";
  return "neutral";
}

function humanize(token: string): string {
  const text = token.replace(/[_-]+/g, " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : token;
}

function formatTime(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d.toLocaleString();
}

function Field({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="break-words font-mono text-[13px] text-ink">{value ?? "—"}</dd>
    </div>
  );
}

function StageCard({ stage }: { stage: LifecycleStage }) {
  const status = STAGE_STATUS[stage.status];
  return (
    <Card data-testid="lifecycle-stage">
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-ink">{stage.title}</h3>
            {stage.purpose && <p className="mt-0.5 text-xs text-muted">{stage.purpose}</p>}
          </div>
          <Badge variant={status.tone}>{status.label}</Badge>
        </div>
        {stage.checks.length === 0 ? (
          <p className="mt-3 text-sm text-muted">This stage reported no checks.</p>
        ) : (
          <ul className="mt-3 divide-y divide-line rounded-md border border-line">
            {stage.checks.map((check) => (
              <li key={check.id} className="flex flex-col gap-0.5 px-3 py-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-ink">{check.label}</div>
                  {check.evidence && <div className="break-words text-xs text-muted">{check.evidence}</div>}
                </div>
                <Badge variant={checkTone(check.status)} size="sm" className="self-start">
                  {humanize(check.status)}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

export default function AdminDeploymentPage() {
  const [state, setState] = useState<Load>({ kind: "loading" });

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchDeploymentPosture();
    setState(result.ok ? { kind: "ready", posture: result.data } : { kind: "failed", failure: result });
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const checkedAt = state.kind === "ready" ? formatTime(state.posture.generatedAt) : undefined;

  return (
    <div>
      <PageHeader
        title="Deployment"
        subtitle={checkedAt ? `What is running and whether it is ready. Checked ${checkedAt}.` : "What is running and whether it is ready."}
        actions={
          <Link
            to="/admin/services"
            className="inline-flex min-h-9 items-center text-sm font-semibold text-primary hover:underline"
          >
            View services
          </Link>
        }
      />

      {state.kind === "loading" && <LoadingState rows={4} />}

      {state.kind === "failed" && <AdminConsoleFailureState failure={state.failure} subject="deployment posture" onRetry={() => void load()} />}

      {state.kind === "ready" && (
        <div className="flex flex-col gap-6">
          <Card data-testid="deployment-identity">
            <CardBody>
              <CardTitle>Running release</CardTitle>
              <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="Release" value={state.posture.releaseVersion} />
                <Field label="Commit" value={state.posture.commitSha?.slice(0, 12)} />
                <Field label="Environment" value={state.posture.environment} />
                <Field label="Region" value={state.posture.region} />
                <Field label="Cluster" value={state.posture.cluster} />
              </dl>
            </CardBody>
          </Card>

          {state.posture.summary.serviceCount !== undefined && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
              <StatCard value={String(state.posture.summary.serviceCount)} label="Services" />
              <StatCard value={String(state.posture.summary.up ?? "—")} label="Up" tone="success" />
              <StatCard value={String(state.posture.summary.degraded ?? "—")} label="Degraded" tone={(state.posture.summary.degraded ?? 0) > 0 ? "warning" : "default"} />
              <StatCard value={String(state.posture.summary.down ?? "—")} label="Down" tone={(state.posture.summary.down ?? 0) > 0 ? "danger" : "default"} />
            </div>
          )}

          <section aria-labelledby="lifecycle-heading">
            <h2 id="lifecycle-heading" className="mb-2 text-base font-bold text-ink">Lifecycle</h2>
            {state.posture.lifecycle.length === 0 ? (
              <div data-testid="lifecycle-empty" className="rounded-lg border border-line bg-surface px-4 py-8 text-center text-sm text-muted">
                The console reported no lifecycle stages for this deployment.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {state.posture.lifecycle.map((stage) => (
                  <StageCard key={stage.id} stage={stage} />
                ))}
              </div>
            )}
          </section>

          {(state.posture.helpSections.length > 0 || state.posture.handoffs.length > 0) && (
            <section aria-labelledby="triage-heading">
              <h2 id="triage-heading" className="mb-2 text-base font-bold text-ink">When something goes wrong</h2>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {state.posture.helpSections.map((section) => (
                  <Card key={section.title} data-testid="help-section">
                    <CardBody>
                      <h3 className="text-sm font-bold text-ink">{section.title}</h3>
                      {section.checks.length > 0 && (
                        <ul className="mt-2 list-disc pl-5 text-sm text-ink">
                          {section.checks.map((check) => (
                            <li key={check}>{check}</li>
                          ))}
                        </ul>
                      )}
                      {section.escalation && <p className="mt-2 text-xs text-muted">{section.escalation}</p>}
                    </CardBody>
                  </Card>
                ))}
              </div>
              {state.posture.handoffs.length > 0 && (
                <ul className="mt-3 divide-y divide-line rounded-lg border border-line bg-surface">
                  {state.posture.handoffs.map((handoff) => (
                    <li key={handoff.id} className="px-4 py-3" data-testid="handoff">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-ink">{handoff.target}</span>
                        {handoff.url ? (
                          <a
                            href={handoff.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex min-h-9 items-center gap-1 text-sm font-semibold text-primary hover:underline"
                          >
                            Open <ExternalLink aria-hidden className="size-3.5" />
                          </a>
                        ) : (
                          <span className="text-xs text-muted">No link configured for this deployment</span>
                        )}
                      </div>
                      {handoff.useWhen && <p className="mt-0.5 text-xs text-muted">{handoff.useWhen}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </div>
      )}
    </div>
  );
}
